"use client";

import { useEffect, useRef } from "react";

export interface OrganizationValueConfirmModalProps {
  label: string;
  formattedValue: string;
  busy: boolean;
  error: string | null;
  onConfirm(): void;
  onCancel(): void;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled]):not([tabindex='-1'])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(",");

function focusableElements(dialog: HTMLElement): HTMLElement[] {
  return Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

export function OrganizationValueConfirmModal({
  label,
  formattedValue,
  busy,
  error,
  onConfirm,
  onCancel,
}: OrganizationValueConfirmModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCancelRef = useRef(onCancel);
  const busyRef = useRef(busy);
  onCancelRef.current = onCancel;
  busyRef.current = busy;

  useEffect(() => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (!busyRef.current) onCancelRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;

      const focusable = focusableElements(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      const active = document.activeElement;
      const leavingAtStart = event.shiftKey && active === first;
      const leavingAtEnd = !event.shiftKey && active === last;
      const focusIsOutside = !dialog.contains(active);

      if (leavingAtStart || leavingAtEnd || focusIsOutside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    (dialog ? focusableElements(dialog)[0] : null)?.focus();
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, []);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape and Tab are handled by the window listener so dismissal works even if focus leaves the dialog.
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Update organization value?"
      tabIndex={-1}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 p-6"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-[480px] overflow-hidden rounded-(--radius-container) border border-(--border-subtle) bg-(--bg-white) shadow-(--shadow-lg)">
        <div className="px-5 py-4">
          <h2 className="m-0 text-[16px] font-bold text-(--fg-primary)">
            Update organization value?
          </h2>
          <p className="mb-0 mt-2 text-[13px] leading-[1.5] text-(--fg-secondary)">
            {label} will be updated to <strong>{formattedValue}</strong> on the
            organization profile. This opportunity will be recalculated
            immediately.
          </p>
          {error && (
            <p
              role="alert"
              className="mb-0 mt-3 rounded-(--radius-field) bg-(--status-error-bg) px-3 py-2 text-[12px] text-(--status-error-text)"
            >
              {error}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-(--border-subtle) px-5 py-3">
          <button
            type="button"
            disabled={busy}
            className="rounded-(--radius-field) border border-(--border-default) bg-(--bg-white) px-3.5 py-2 text-[13px] font-semibold text-(--fg-secondary) hover:bg-(--bg-hover) disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            className="rounded-(--radius-field) border-0 bg-(--brand-navy) px-3.5 py-2 text-[13px] font-semibold text-(--fg-inverse) hover:bg-(--steel-110) disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onConfirm}
          >
            Update organization
          </button>
        </div>
      </div>
    </div>
  );
}
